import { Component } from '@theme/component';
import { CartItemsComponent } from '@theme/component-cart-items';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} ProtectionRefs
 * @property {HTMLFormElement} form
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement} status
 * @extends {Component<ProtectionRefs>}
 */
class WalkiiesCartProtection extends Component {
  requiredRefs = ['form', 'toggle', 'status'];
  static pending = false;
  /** @type {AbortController | undefined} */
  #request;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
  }

  /** Explicit add/remove only; rendered forms also work without enhancement. @param {SubmitEvent} event */
  async submit(event) {
    event.preventDefault();
    event.stopPropagation();
    if (WalkiiesCartProtection.pending) return;

    const owner = this.closest('cart-items-component');
    const sectionId = this.dataset.sectionId;
    if (!(owner instanceof CartItemsComponent) || !sectionId) return;

    WalkiiesCartProtection.pending = true;
    const controller = new AbortController();
    this.#request = controller;
    const remove = this.dataset.selected === 'true';
    const returnFocus = this.contains(document.activeElement);
    const message = remove ? this.dataset.removed : this.dataset.added;
    const errorMessage = this.dataset.error;
    const deferred = CartLinesUpdateEvent.createPromise();
    // A rejected cart event must not create an unhandled rejection if no consumer is mounted.
    deferred.promise.catch(() => {});
    /** @type {Array<{control: HTMLButtonElement | HTMLInputElement, disabled: boolean}>} */
    let controls = [];

    try {
      await Promise.all(CartItemsComponent.pendingQuantityUpdates);
      if (!this.isConnected) return;
      owner.setAttribute('aria-busy', 'true');
      controls = Array.from(owner.querySelectorAll('button, input')).filter(
        (control) => control instanceof HTMLButtonElement || control instanceof HTMLInputElement
      ).map((control) => ({ control, disabled: control.disabled }));
      for (const { control } of controls) control.disabled = true;
      this.refs.status.textContent = '';

      const cartUrl = new URL(`${Theme.routes.cart_url}.js`, window.location.origin);
      const cartResponse = await fetch(cartUrl, { signal: controller.signal });
      if (!cartResponse.ok) throw new Error(errorMessage);
      const before = await cartResponse.json();
      const line = before.items.find((item) => String(item.variant_id) === this.dataset.variantId);
      const parent = before.items.find((item) => String(item.product_id) === this.dataset.parentProductId && !item.parent_relationship);
      if (!remove && !line && !parent) throw new Error(errorMessage);

      owner.dispatchEvent(new CartLinesUpdateEvent({
        action: remove ? 'remove' : 'add',
        context: 'cart',
        lines: remove ? [{ id: line?.key ?? '', quantity: 0 }] : [{ merchandiseId: this.dataset.variantId, quantity: 1 }],
        promise: deferred.promise,
      }));

      sectionRenderer.abortRender(sectionId);
      let data;
      if ((remove && line) || (!remove && !line)) {
        const endpoint = remove ? Theme.routes.cart_change_url : Theme.routes.cart_add_url;
        const body = remove
          ? { line: before.items.indexOf(line) + 1, quantity: 0 }
          : { items: [{ id: Number(this.dataset.variantId), quantity: 1, parent_line_key: parent.key }] };
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: [sectionId], sections_url: window.location.pathname }),
          signal: controller.signal,
        });
        data = await response.json();
        if (!response.ok || data.status || data.errors) throw new Error(errorMessage);
      }

      const afterResponse = await fetch(cartUrl, { signal: controller.signal });
      if (!afterResponse.ok) throw new Error(errorMessage);
      const after = await afterResponse.json();
      const html = data?.sections?.[sectionId];
      if (html) await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      else await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration', injectStylesheet: true });

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(after),
        detail: { sections: data?.sections, items: after.items, itemCount: after.item_count, source: 'walkiies-protection', didError: false },
      });
      const current = owner.querySelector('walkiies-cart-protection-component');
      const status = current?.querySelector('[ref="status"]');
      if (status) status.textContent = message ?? '';
      if (returnFocus && owner.closest('dialog')?.open) {
        const button = current?.querySelector('button');
        if (button instanceof HTMLButtonElement) button.focus({ preventScroll: true });
      }
    } catch (error) {
      deferred.reject(error);
      if (error?.name !== 'AbortError' && this.isConnected) this.refs.status.textContent = errorMessage ?? '';
    } finally {
      owner.removeAttribute('aria-busy');
      for (const { control, disabled } of controls) {
        if (control.isConnected) control.disabled = disabled;
      }
      if (this.#request === controller) this.#request = undefined;
      WalkiiesCartProtection.pending = false;
    }
  }
}

if (!customElements.get('walkiies-cart-protection-component')) {
  customElements.define('walkiies-cart-protection-component', WalkiiesCartProtection);
}
